import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  TextField,
} from "@mui/material";
import { Label } from "@/components/Label";
import type {
  IInheritanceReuseFilterCondition,
  InheritanceReuseCityEntity,
  InheritanceReusePrefectureEntity,
} from "@/features/inheritanceReuse/types";
import {
  DISTANCE_CATEGORY_OPTIONS,
  REAL_ESTATE_TYPE_OPTIONS,
  SALE_RANK_MAX,
} from "@/features/inheritanceReuse/utils/constants";
import {
  hasFilterRangeErrors,
  validateFilterRanges,
} from "@/features/inheritanceReuse/utils/buildRequest";

interface IProps {
  value: IInheritanceReuseFilterCondition;
  onChange: (value: IInheritanceReuseFilterCondition) => void;
  onSearch: () => void;
  onReset: () => void;
  prefectures: InheritanceReusePrefectureEntity[];
  prefecturesLoading: boolean;
  cities: InheritanceReuseCityEntity[];
  citiesLoading: boolean;
}

const RATE_OPTIONS: number[] = Array.from(
  { length: SALE_RANK_MAX },
  (_, i) => i + 1
);

/** ラベル＋コントロールを縦積みする共通 Grid セル（全フィールド 3 列で高さを揃える）。 */
const FieldGridItem: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Grid item xs={12} sm={6} md={4}>
    {children}
  </Grid>
);

const placeholderStyle = { color: "text.disabled" } as const;

/** 範囲入力（左〜右）を等幅・中央揃えで並べる共通レイアウト。 */
const RangeRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    {children}
  </Stack>
);

const Tilde: React.FC = () => (
  <Box sx={{ color: "text.secondary", flexShrink: 0, lineHeight: "40px" }}>
    〜
  </Box>
);

/**
 * 相続後リユース候補のフィルタ UI。
 * 既存プロダクトの検索フォーム流儀（Label＋Grid、右寄せ検索/クリア）に合わせ、全フィールドを
 * size="small" で高さ・幅を統一。範囲入力（築年・取得日・相続登記日）は左右等幅＋中央「〜」で揃える。
 * 都道府県未選択時・範囲(from<=to)不正時は検索無効＋該当欄にエラー表示。
 */
export const InheritanceReuseFilterPanel: React.FC<IProps> = ({
  value,
  onChange,
  onSearch,
  onReset,
  prefectures,
  prefecturesLoading,
  cities,
  citiesLoading,
}) => {
  const update = <K extends keyof IInheritanceReuseFilterCondition>(
    key: K,
    v: IInheritanceReuseFilterCondition[K]
  ): void => {
    onChange({ ...value, [key]: v });
  };

  const handleSelect =
    (key: keyof IInheritanceReuseFilterCondition) =>
    (e: SelectChangeEvent): void => {
      update(key, e.target.value as never);
    };

  // 都道府県変更時は市区町村選択をリセットする（別都道府県の市区が残らないように）。
  const handlePrefectureChange = (e: SelectChangeEvent): void => {
    onChange({ ...value, prefectureName: e.target.value, cityName: "" });
  };

  const rangeErrors = validateFilterRanges(value);
  const searchDisabled =
    value.prefectureName.trim() === "" || hasFilterRangeErrors(rangeErrors);

  return (
    <Box>
      <Grid container spacing={2.5}>
        {/* 都道府県（必須・件数表示） */}
        <FieldGridItem>
          <Label required>都道府県</Label>
          <FormControl fullWidth size="small">
            <Select
              displayEmpty
              value={value.prefectureName}
              onChange={handlePrefectureChange}
              disabled={prefecturesLoading}
              renderValue={(selected) =>
                selected === "" ? (
                  <Box component="span" sx={placeholderStyle}>
                    選択してください
                  </Box>
                ) : (
                  selected
                )
              }
            >
              {prefectures.map((pref) => (
                <MenuItem key={pref.prefectureName} value={pref.prefectureName}>
                  {pref.prefectureName}（{pref.count.toLocaleString()}件）
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FieldGridItem>

        {/* 市区町村（都道府県依存・件数表示） */}
        <FieldGridItem>
          <Label>市区町村</Label>
          <FormControl fullWidth size="small">
            <Select
              displayEmpty
              value={value.cityName}
              onChange={handleSelect("cityName")}
              disabled={value.prefectureName === "" || citiesLoading}
              renderValue={(selected) =>
                selected === "" ? (
                  <Box component="span" sx={placeholderStyle}>
                    {value.prefectureName === ""
                      ? "都道府県を選択"
                      : "指定なし"}
                  </Box>
                ) : (
                  selected
                )
              }
            >
              <MenuItem value="">指定なし</MenuItem>
              {cities.map((city) => (
                <MenuItem key={city.cityName} value={city.cityName}>
                  {city.cityName}（{city.count.toLocaleString()}件）
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FieldGridItem>

        {/* 種別 */}
        <FieldGridItem>
          <Label>種別</Label>
          <FormControl fullWidth size="small">
            <Select
              displayEmpty
              value={value.realEstateType}
              onChange={handleSelect("realEstateType")}
              renderValue={(selected) =>
                selected === "" ? (
                  <Box component="span" sx={placeholderStyle}>
                    指定なし
                  </Box>
                ) : (
                  REAL_ESTATE_TYPE_OPTIONS.find((o) => o.value === selected)
                    ?.label ?? selected
                )
              }
            >
              <MenuItem value="">指定なし</MenuItem>
              {REAL_ESTATE_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FieldGridItem>

        {/* 距離区分 */}
        <FieldGridItem>
          <Label>距離区分</Label>
          <FormControl fullWidth size="small">
            <Select
              displayEmpty
              value={value.distanceCategory}
              onChange={handleSelect("distanceCategory")}
              renderValue={(selected) =>
                selected === "" ? (
                  <Box component="span" sx={placeholderStyle}>
                    指定なし
                  </Box>
                ) : (
                  DISTANCE_CATEGORY_OPTIONS.find((o) => o.value === selected)
                    ?.label ?? selected
                )
              }
            >
              <MenuItem value="">指定なし</MenuItem>
              {DISTANCE_CATEGORY_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FieldGridItem>

        {/* 売却期待度★以上 */}
        <FieldGridItem>
          <Label>売却期待度（★以上）</Label>
          <FormControl fullWidth size="small">
            <Select
              displayEmpty
              value={value.minSaleRank}
              onChange={handleSelect("minSaleRank")}
              renderValue={(selected) =>
                selected === "" ? (
                  <Box component="span" sx={placeholderStyle}>
                    指定なし
                  </Box>
                ) : (
                  `${"★".repeat(Number(selected))}以上`
                )
              }
            >
              <MenuItem value="">指定なし</MenuItem>
              {RATE_OPTIONS.map((n) => (
                <MenuItem key={n} value={String(n)}>
                  {"★".repeat(n)}以上
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FieldGridItem>

        {/* 次相続スコア以上 */}
        <FieldGridItem>
          <Label>次相続スコア（以上）</Label>
          <TextField
            fullWidth
            size="small"
            type="number"
            placeholder="指定なし"
            inputProps={{ min: 0, step: 1 }}
            value={value.minInheritanceScore}
            error={rangeErrors.minInheritanceScore !== undefined}
            helperText={rangeErrors.minInheritanceScore}
            onChange={(e) => {
              update("minInheritanceScore", e.target.value);
            }}
          />
        </FieldGridItem>

        {/* 所有者数上限 */}
        <FieldGridItem>
          <Label>所有者数（上限）</Label>
          <TextField
            fullWidth
            size="small"
            type="number"
            placeholder="指定なし"
            inputProps={{ min: 1, step: 1 }}
            value={value.maxOwnerCount}
            error={rangeErrors.maxOwnerCount !== undefined}
            helperText={rangeErrors.maxOwnerCount}
            onChange={(e) => {
              update("maxOwnerCount", e.target.value);
            }}
          />
        </FieldGridItem>

        {/* 築年（下限〜上限） */}
        <FieldGridItem>
          <Label>築年</Label>
          <RangeRow>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="下限"
              inputProps={{ min: 0, step: 1 }}
              value={value.buildYearFrom}
              error={rangeErrors.buildYearFrom !== undefined}
              helperText={rangeErrors.buildYearFrom}
              onChange={(e) => {
                update("buildYearFrom", e.target.value);
              }}
            />
            <Tilde />
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="上限"
              inputProps={{ min: 0, step: 1 }}
              value={value.buildYearTo}
              error={rangeErrors.buildYearTo !== undefined}
              helperText={rangeErrors.buildYearTo}
              onChange={(e) => {
                update("buildYearTo", e.target.value);
              }}
            />
          </RangeRow>
        </FieldGridItem>

        {/* 推定土地評価額（円・下限〜上限） */}
        <FieldGridItem>
          <Label>推定土地評価額（円）</Label>
          <RangeRow>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="下限"
              inputProps={{ min: 0, step: 1 }}
              value={value.minEstimatedLandValue}
              error={rangeErrors.minEstimatedLandValue !== undefined}
              helperText={rangeErrors.minEstimatedLandValue}
              onChange={(e) => {
                update("minEstimatedLandValue", e.target.value);
              }}
            />
            <Tilde />
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="上限"
              inputProps={{ min: 0, step: 1 }}
              value={value.maxEstimatedLandValue}
              error={rangeErrors.maxEstimatedLandValue !== undefined}
              helperText={rangeErrors.maxEstimatedLandValue}
              onChange={(e) => {
                update("maxEstimatedLandValue", e.target.value);
              }}
            />
          </RangeRow>
        </FieldGridItem>

        {/* 取得日（以降〜以前） */}
        <FieldGridItem>
          <Label>取得日</Label>
          <RangeRow>
            <TextField
              fullWidth
              size="small"
              type="date"
              value={value.acquiredFrom}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => {
                update("acquiredFrom", e.target.value);
              }}
            />
            <Tilde />
            <TextField
              fullWidth
              size="small"
              type="date"
              value={value.acquiredTo}
              InputLabelProps={{ shrink: true }}
              error={rangeErrors.acquiredTo !== undefined}
              helperText={rangeErrors.acquiredTo}
              onChange={(e) => {
                update("acquiredTo", e.target.value);
              }}
            />
          </RangeRow>
        </FieldGridItem>

        {/* 相続登記日（以降〜以前） */}
        <FieldGridItem>
          <Label>相続登記日</Label>
          <RangeRow>
            <TextField
              fullWidth
              size="small"
              type="date"
              value={value.inheritanceFrom}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => {
                update("inheritanceFrom", e.target.value);
              }}
            />
            <Tilde />
            <TextField
              fullWidth
              size="small"
              type="date"
              value={value.inheritanceTo}
              InputLabelProps={{ shrink: true }}
              error={rangeErrors.inheritanceTo !== undefined}
              helperText={rangeErrors.inheritanceTo}
              onChange={(e) => {
                update("inheritanceTo", e.target.value);
              }}
            />
          </RangeRow>
        </FieldGridItem>

        {/* 居住・空き家（矛盾する組合せのため排他。片方 ON でもう片方は自動 OFF） */}
        <Grid item xs={12}>
          <Label>絞り込み</Label>
          <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={value.isOccupant}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    // 居住のみ ON のときは空き家のみを OFF（居住≠空き家で矛盾するため）。
                    onChange({
                      ...value,
                      isOccupant: checked,
                      emptyHouse: checked ? false : value.emptyHouse,
                    });
                  }}
                />
              }
              label="居住のみ"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={value.emptyHouse}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    onChange({
                      ...value,
                      emptyHouse: checked,
                      isOccupant: checked ? false : value.isOccupant,
                    });
                  }}
                />
              }
              label="空き家のみ"
            />
          </Stack>
        </Grid>
      </Grid>

      {/* ボタン（右寄せ・既存フォーム流儀） */}
      <Box
        sx={{
          mt: 3,
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.5,
        }}
      >
        <Button variant="outlined" onClick={onReset}>
          クリア
        </Button>
        <Button
          variant="contained"
          onClick={onSearch}
          disabled={searchDisabled}
        >
          この条件で検索
        </Button>
      </Box>
    </Box>
  );
};
